import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { LegalFooter } from "@/components/LegalFooter";

/**
 * Withdrawal and refund policy for Xenomons Gold purchases.
 * Meets Paddle's refund policy requirements (14–90 days, no "no refund" wording).
 */
export const Route = createFileRoute("/widerruf")({
  head: () => ({
    meta: [
      { title: "Refunds & Withdrawal – Xenomons" },
      {
        name: "description",
        content:
          "Refund policy and 30-day money-back guarantee for Gold purchases in Xenomons. Processed by Paddle as Merchant of Record.",
      },
      { property: "og:title", content: "Refunds & Withdrawal – Xenomons" },
      {
        property: "og:description",
        content: "How to withdraw or get a refund for Gold purchases in Xenomons.",
      },
      { property: "og:url", content: "https://xenomons.online/widerruf" },
    ],
    links: [
      { rel: "canonical", href: "https://xenomons.online/widerruf" },
      { rel: "alternate", hrefLang: "de", href: "https://xenomons.online/widerruf" },
      { rel: "alternate", hrefLang: "en", href: "https://xenomons.online/widerruf" },
      { rel: "alternate", hrefLang: "x-default", href: "https://xenomons.online/widerruf" },
    ],
  }),
  component: Widerruf,
});

type Section = {
  title: string;
  paragraphs?: string[];
  items?: string[];
};

function Widerruf() {
  const { t } = useTranslation("legal");
  const sections = t("refund.sections", { returnObjects: true }) as Section[];

  return (
    <main className="min-h-screen flex flex-col bg-background">
      <div className="flex-1 px-6 py-10">
        <div className="mx-auto max-w-2xl">
          <Link to="/" className="text-sm font-medium text-primary hover:underline">
            {t("back")}
          </Link>
          <h1 className="mt-4 font-display text-4xl font-extrabold tracking-tight">
            {t("refund.title")}
          </h1>
          <p className="mt-3 text-xs italic text-muted-foreground">
            {t("refund.status")}
          </p>

          <section className="mt-6 space-y-6 text-sm leading-relaxed">
            {sections.map((s, i) => (
              <div key={i}>
                <h2 className="text-lg font-bold">{s.title}</h2>
                {s.paragraphs?.map((p, j) => (
                  <p key={j} className={j === 0 ? "" : "mt-2"}>
                    {p}
                  </p>
                ))}
                {s.items && (
                  <ul className="list-disc pl-5 space-y-2 mt-2">
                    {s.items.map((it, k) => (
                      <li key={k}>{it}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </section>
        </div>
      </div>
      <LegalFooter />
    </main>
  );
}
